import { jwt } from "#webapp/src/state/local-storage";
import { EventColorCategory } from "@clockwise/client-commons/src/util/event-category-coloring";
import { createTagForMutation, Tags } from "@clockwise/client-commons/src/util/event-tag";
import { isImpersonated } from "@clockwise/client-commons/src/util/jwt";
import { isNotNull } from "@clockwise/client-commons/src/util/null";
import { RepeatingEventSaveOption } from "@clockwise/schema";
import {
  useGatewayMutation,
  useGatewayQuery,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import React, { ChangeEvent, useState } from "react";
import toast from "react-hot-toast";
import { useToggle } from "usehooks-ts";
import { shouldUpdateThisAndFutureEvents } from "../chat/ai-chat/utils/getRecurrenceToSave";
import { getDeleteEventCopy, getSubmitButtonCopy } from "../event-card/utils/getSubmitButtonCopy";
import useEditExistingEvent from "../web-app-calendar/hooks/useEditExistingEvent";
import {
  formatRecurrenceToSave,
  formatRecurrenceToString,
  getBodyForSaveChanges,
  getDisabledSaveOptions,
} from "../web-app-calendar/hooks/useEditExistingEvent.util";
import { CategoryOption, EventDetails } from "../web-app-calendar/hooks/useEventDetails";
import { EnableOrDisableTeamSyncDocument } from "./__generated__/EnableOrDisableTeamSync.v2.generated";
import { UserTeamNamesDocument } from "./__generated__/UserTeamNames.v2.generated";
import { Divider } from "./atoms/Divider";
import { CardWrapper } from "./CardWrapper";
import { useEventAttendees } from "./hooks/EventAttendeesContext";
import { useReadEventTimeDetails } from "./hooks/EventCardContext";
import { AllDay } from "./molecules/ECAllDay";
import { ECCategorySelector as CategorySelector } from "./molecules/ECCategorySelect";
import { ECFooter } from "./molecules/ECFooter";
import { Recurrence, SelectableRecurrenceOption } from "./molecules/ECRecurrence";
import { ECTeamSyncSection as TeamSyncSection } from "./molecules/ECTeamSyncSection";
import { Time } from "./molecules/ECTime";
import { ECTitle as Title } from "./molecules/ECTitle";
import { EventCardMode, EventPermissionsInfo } from "./types";

type Props = Pick<
  EventDetails,
  "title" | "category" | "recurrence" | "attendeesOmitted" | "eventPermissions"
> & {
  colorSettings?: CategoryOption[];
  eventCalendarId: string;
  id: string;
  isOwnEvent: boolean;
  isRecurring: boolean;
  isSynced: boolean;
  permissions: EventPermissionsInfo;
};

export const OutOfOfficeCard = ({
  attendeesOmitted,
  category: initCategory = EventColorCategory.OutOfOffice,
  colorSettings,
  eventCalendarId,
  eventPermissions,
  id = "",
  isOwnEvent,
  isRecurring,
  isSynced: initIsSynced,
  permissions,
  recurrence: initRecurrence,
  title: initTitle = "Out of Office",
}: Props) => {
  const updateActiveEvent = useUpdateActiveEvent();
  const { attendees } = useEventAttendees();
  const { startTime, timesHaveChanged } = useReadEventTimeDetails();

  const [syncing, toggleSyncing] = useToggle(initIsSynced);
  const [title, setTitle] = useState<string>(initTitle);
  const [category, setCategory] = useState<EventColorCategory>(initCategory);
  const [eventRecurrence, setEventRecurrence] = useState<SelectableRecurrenceOption>("None");

  const { data: userTeamData } = useGatewayQuery(UserTeamNamesDocument, {
    skip: !isOwnEvent,
  });
  const teams =
    userTeamData?.currentUser?.teams.filter(isNotNull).filter((team) => team.manageTeamCalendar) ??
    [];

  const { onSaveEventChanges, savingChanges } = useEditExistingEvent(
    id,
    eventCalendarId,
    isRecurring,
    {
      onEditCompleted: () => {
        toast.success("Event saved");
        updateActiveEvent(null);
      },
      onEditError: () => toast.error("Event failed to save"),
    },
  );

  const [updateTeamSync] = useGatewayMutation(EnableOrDisableTeamSyncDocument, {
    onCompleted: () => {
      toast.success("Event sync settings updated");
      updateActiveEvent(null);
    },
    onError: () => toast.error("Sorry, event sync settings not updated"),
  });

  const initRecurrenceText = formatRecurrenceToString(initRecurrence);

  const handleClose = () => {
    updateActiveEvent(null);
  };

  const handleEventNameChange = (evnt: ChangeEvent<HTMLInputElement>) =>
    setTitle(() => evnt.target.value);

  const handleChangeCategory = (newCategory: EventColorCategory) => {
    setCategory(newCategory);
  };

  const handleSubmit = (saveOption: RepeatingEventSaveOption) => {
    void asyncSubmit(saveOption);
  };

  const asyncSubmit = async (saveOption: RepeatingEventSaveOption) => {
    if (changesMap.category || changesMap.eventRecurrence || changesMap.times || changesMap.title) {
      await onSaveEventChanges(
        getBodyForSaveChanges(
          {
            recurrence: formatRecurrenceToSave(eventRecurrence, startTime),
            sendNotifications: false,
            // DevNote(AI-5543): disabling time temporarily as it is causing unpredictable behavior
            // time: Interval.fromDateTimes(startTime, endTime).toISO(),
            title,
            repeatingEventSaveOption: shouldUpdateThisAndFutureEvents(
              saveOption,
              initRecurrence,
              eventRecurrence,
            ),
            category: createTagForMutation(Tags.EventColoringCategory, category),
          },
          eventPermissions,
        ),
      );
    }
    if (changesMap.teamSync) {
      await updateTeamSync({
        variables: {
          externalEventId: id,
          enabled: syncing,
        },
      });
    }
  };

  const changesMap = {
    category: category !== initCategory,
    eventRecurrence: eventRecurrence !== initRecurrenceText,
    teamSync: syncing !== initIsSynced,
    times: timesHaveChanged,
    title: title !== initTitle,
  };

  const changesCount = getChangesCount(changesMap);

  const isImpersonatedUser = isImpersonated(jwt.get());
  const userIsOnlyAttendee = attendees.length <= 1 && !attendeesOmitted;

  const disabledRecurrenceSaveOptions = getDisabledSaveOptions({
    hasFlexBeenModified: false,
    hasRecurrenceBeenModified: changesMap.eventRecurrence,
    isDescriptionOmitted: false,
    canModifyFuture: permissions.canModifyFuture,
    canModifyAll: permissions.canModifyAll,
  });

  return (
    <CardWrapper
      changesCount={changesCount}
      header={<div className="cw-font-semibold">Event</div>}
      onClose={handleClose}
      mode={EventCardMode.default}
      footer={
        <ECFooter
          canDelete={permissions.canDelete}
          canModify={permissions.canModify}
          canRemove={permissions.canRemove}
          deletionCopy={getDeleteEventCopy(permissions.canDelete, isImpersonatedUser)}
          disabled={savingChanges}
          disabledRecurrenceSaveOptions={disabledRecurrenceSaveOptions}
          onSubmit={handleSubmit}
          showDropdownOnSave={
            eventRecurrence !== "None" && (permissions.canModifyAll || permissions.canModifyFuture)
          }
          submissionCopy={getSubmitButtonCopy(false, !userIsOnlyAttendee, isImpersonatedUser)}
          updatesPresent={changesCount > 0}
        />
      }
    >
      <Title eventName={title} onEventNameChange={handleEventNameChange} readOnly={!isOwnEvent} />
      <Time
        // DevNote(AI-5543): disabling time temporarily as it is causing unpredictable behavior
        // readOnly={!isOwnEvent}
        readOnly
        timesHaveChanged={timesHaveChanged}
      />
      <AllDay
        // DevNote(AI-5543): disabling time temporarily as it is causing unpredictable behavior
        // readOnly={!isOwnEvent}
        readOnly
      />
      <CategorySelector
        allColorSettings={colorSettings}
        currentCategory={category}
        onCategoryChange={handleChangeCategory}
        readOnly={!isOwnEvent}
      />
      <Recurrence
        readOnly={!isOwnEvent}
        fetchedRecurrenceString={initRecurrenceText}
        selectedOption={eventRecurrence}
        onSelect={setEventRecurrence}
        showChanges={changesMap.eventRecurrence}
      />
      {isOwnEvent && (
        <>
          <Divider />
          <TeamSyncSection syncing={syncing} teams={teams} onChange={toggleSyncing} />
        </>
      )}
    </CardWrapper>
  );
};

const getChangesCount = (obj: { [key: string]: boolean }) => {
  let count = 0;
  for (const k in obj) {
    if (obj[k]) {
      count++;
    }
  }
  return count;
};
